import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import protocolRegistrationService from '../../services/protocolRegistration.service';

const recruitmentStatusOptions = [
  'Not yet Recruiting',
  'Recruiting',
  'Enrolling by invitation',
  'Active, not recruiting',
  'Completed',
  'Suspended',
  'Terminated',
  'Withdrawn',
];

const yesNoOptions = ['Yes', 'No'];
const expandedAccessOptions = ['Yes', 'No', 'Unknown'];
const irbStatusOptions = [
  'Request Not yet Submitted',
  'Submitted-Pending',
  'Submitted-approved',
  'Submitted-Denied',
  'Submission Not Required',
];

const primaryPurposeOptions = [
  'Treatment',
  'Prevention',
  'Diagnostic',
  'Supportive Care',
  'Screening',
  'Health Services Research',
  'Basic Science',
  'Device Feasibility',
  'Other',
];

const studyPhaseOptions = [
  'N/A',
  'Early Phase 1',
  'Phase 1',
  'Phase 1/Phase 2',
  'Phase 2',
  'Phase 2/Phase 3',
  'Phase 3',
  'Phase 4',
];

const interventionalModelOptions = [
  'Single Group',
  'Parallel',
  'Cross-over',
  'Factorial',
  'Sequential',
];

const allocationOptions = ['N/A', 'Randomized', 'Non Randomized'];

const observationalModelOptions = [
  'Cohort',
  'Case-Control',
  'Case-Only',
  'Case-Crossover',
  'Ecologic / Community Studies',
  'Family Based',
  'Other',
];

const timePerspectiveOptions = [
  'Retrospective',
  'Prospective',
  'Cross-Sectional',
  'Other',
];

const biospecimenRetentionOptions = [
  'None Retained',
  'Samples with DNA',
  'Samples without DNA',
];

const armTypes = ['Experimental', 'Active Comparator', 'Placebo Comparator', 'Sham Comparator', 'No Intervention', 'Other'];
const interventionTypes = [
  'Drug',
  'Device',
  'Biological/Vaccine',
  'Procedure/Surgery',
  'Radiation',
  'Behavioral',
  'Genetics',
  'Dietary Supplements',
  'Diagnostic Tests',
  'Other',
];

const initialForm = {
  study_identification: {
    unique_protocol_id: '',
    brief_title: '',
    official_title: '',
    secondary_id_type: '',
    secondary_id_description: '',
    study_type: '',
  },
  study_status: {
    record_verification_date: '',
    overall_recruitment_status: '',
    withdrawal_reason: '',
    study_start_date: '',
    primary_completion_date: '',
    study_completion_date: '',
  },
  sponsors_collaborators: {
    investigator_name: '',
    investigator_title: '',
    investigator_affiliation: '',
    sponsor_name: '',
    collaborators: '',
  },
  oversight: {
    fda_regulated_drug_product: '',
    fda_regulated_device_product: '',
    has_ind_ide: '',
    ind_ide_number: '',
    ind_serial_number: '',
    expanded_access: '',
    product_manufactured_exported_us: '',
    human_subject_review: '',
    human_subject_protection_status: '',
    board_approval_number: '',
    board_name: '',
    board_affiliation: '',
    board_contact: '',
    data_monitoring_fda_regulatory_intervention: '',
    data_monitoring_section801_clinical_trials: '',
  },
  study_description: {
    brief_summary: '',
    detailed_description: '',
  },
  condition_keywords: {
    primary_condition: '',
    keywords: '',
  },
  study_design: {
    interventional: {
      primary_purpose: '',
      study_phase: '',
      study_model: '',
      study_model_description: '',
      number_of_arms: '',
      masking_enabled: '',
      masking_roles: {
        participants: false,
        care_providers: false,
        investigators: false,
        outcomes_assessors: false,
      },
      masking_description: '',
      allocation: '',
      enrollment: '',
    },
    observational: {
      study_model: '',
      time_perspective: '',
      biospecimen_retention: '',
      biospecimen_description: '',
      enrollment: '',
      target_followup_duration: '',
      number_of_groups: '',
    },
  },
  arms_groups_interventions: {
    arms: [{ title: '', type: '', description: '' }],
    groups: [{ label: '', description: '' }],
    interventions: [{ intervention_type: '', name: '', description: '', arm_group_cross_reference: '' }],
  },
  outcomes: {
    primary: [{ title: '', description: '', timeframe: '' }],
    secondary: [{ title: '', description: '', timeframe: '' }],
    other: [{ title: '', description: '', timeframe: '' }],
  },
  eligibility: {
    sex: '',
    gender_based: '',
    gender_description: '',
    age_min: '',
    age_min_unit: '',
    age_max: '',
    age_max_unit: '',
    healthy_volunteers: '',
    eligibility_criteria: '',
    study_population_description: '',
    sampling_method: '',
  },
  contacts_locations_investigators: {
    central_contact: {
      first_name: '', middle_initials: '', last_name: '', degree: '', phone: '', phone_ext: '', email: '',
    },
    central_contact_backup: {
      first_name: '', middle_initials: '', last_name: '', degree: '', phone: '', phone_ext: '', email: '',
    },
    study_officials: [{
      first_name: '', middle_initials: '', last_name: '', degree: '', affiliation: '', role: '',
    }],
    facilities: [{
      name: '', city: '', state: '', postal_code: '', country: '', individual_site_status: '',
      contact: { first_name: '', middle_initials: '', last_name: '', degree: '', phone: '', phone_ext: '', email: '' },
      contact_backup: { first_name: '', middle_initials: '', last_name: '', degree: '', phone: '', phone_ext: '', email: '' },
      investigators: [{
        first_name: '', middle_initials: '', last_name: '', degree: '', role: '',
      }],
    }],
  },
  ipd_sharing: {
    plan: '',
    description: '',
    supporting_info_types: '',
    timeframe: '',
    access_criteria: '',
    url: '',
    reason_if_no: '',
  },
  references: {
    citations: [{ pubmed_id: '', citation: '', result_reference: '' }],
    links: [{ url: '', description: '' }],
    available_ipd_information: [{ info_type: '', url: '', identifier: '', comment: '' }],
  },
};

/**
 * XML utility functions for ClinicalTrials.gov XSD conversion
 */

// Escape XML special characters
function escapeXml(unsafe) {
  if (unsafe === null || unsafe === undefined) return '';
  return String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// Convert Yes/No to lowercase yes/no
function yesNoToXml(value) {
  if (value === 'Yes') return 'yes';
  if (value === 'No') return 'no';
  return '';
}

// Format date to YYYY-MM format
function formatDateForXml(dateStr) {
  if (!dateStr) return '';
  // If already in YYYY-MM format, return as is
  if (/^\d{4}-\d{2}$/.test(dateStr)) return dateStr;
  // Try to parse and format
  try {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  } catch {
    return dateStr;
  }
}

// Convert form data to XSD-compliant XML
function convertFormDataToXML(formData, orgName = '') {
  const esc = escapeXml;
  const yesNo = yesNoToXml;
  const fmtDate = formatDateForXml;

  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<study_collection xmlns="http://clinicaltrials.gov/prs" xmlns:rrs="http://clinicaltrials.gov/rrs">\n';
  xml += '  <clinical_study>\n';

  // id_info
  xml += '    <id_info>\n';
  if (orgName) xml += `      <org_name>${esc(orgName)}</org_name>\n`;
  else xml += `      <org_name>${esc('')}</org_name>\n`;
  xml += `      <org_study_id>${esc(formData.study_identification?.unique_protocol_id || '')}</org_study_id>\n`;
  
  // secondary_id
  if (formData.study_identification?.secondary_id_type && formData.study_identification?.secondary_id_description) {
    xml += '      <secondary_id>\n';
    xml += `        <id>${esc(formData.study_identification.secondary_id_description)}</id>\n`;
    if (formData.study_identification.secondary_id_type) {
      xml += `        <id_type>${esc(formData.study_identification.secondary_id_type)}</id_type>\n`;
    }
    xml += '      </secondary_id>\n';
  }
  xml += '    </id_info>\n';

  // brief_title
  if (formData.study_identification?.brief_title) {
    xml += `    <brief_title>${esc(formData.study_identification.brief_title)}</brief_title>\n`;
  }

  // official_title
  if (formData.study_identification?.official_title) {
    xml += `    <official_title>${esc(formData.study_identification.official_title)}</official_title>\n`;
  }

  // sponsors
  if (formData.sponsors_collaborators?.sponsor_name) {
    xml += '    <sponsors>\n';
    xml += '      <lead_sponsor>\n';
    xml += `        <agency>${esc(formData.sponsors_collaborators.sponsor_name)}</agency>\n`;
    xml += '      </lead_sponsor>\n';
    
    // collaborators
    const collaborators = splitList(formData.sponsors_collaborators?.collaborators || '');
    collaborators.forEach(collab => {
      if (collab) {
        xml += '      <collaborator>\n';
        xml += `        <agency>${esc(collab)}</agency>\n`;
        xml += '      </collaborator>\n';
      }
    });

    // responsible_party
    if (formData.sponsors_collaborators?.investigator_name) {
      xml += '      <resp_party>\n';
      xml += '        <resp_party_type>Principal Investigator</resp_party_type>\n';
      xml += `        <investigator_title>${esc(formData.sponsors_collaborators.investigator_title || '')}</investigator_title>\n`;
      xml += `        <investigator_affiliation>${esc(formData.sponsors_collaborators.investigator_affiliation || '')}</investigator_affiliation>\n`;
      xml += '      </resp_party>\n';
    }
    xml += '    </sponsors>\n';
  }

  // oversight_info
  const hasOversight = formData.oversight?.fda_regulated_drug_product || 
                       formData.oversight?.fda_regulated_device_product ||
                       formData.oversight?.has_ind_ide ||
                       formData.oversight?.human_subject_review;
  if (hasOversight) {
    xml += '    <oversight_info>\n';
    
    if (formData.oversight?.fda_regulated_drug_product) {
      xml += `      <fda_regulated_drug>${yesNoToXml(formData.oversight.fda_regulated_drug_product)}</fda_regulated_drug>\n`;
    }
    if (formData.oversight?.fda_regulated_device_product) {
      xml += `      <fda_regulated_device>${yesNoToXml(formData.oversight.fda_regulated_device_product)}</fda_regulated_device>\n`;
    }
    if (formData.oversight?.product_manufactured_exported_us) {
      xml += `      <exported_from_us>${yesNoToXml(formData.oversight.product_manufactured_exported_us)}</exported_from_us>\n`;
    }
    if (formData.oversight?.data_monitoring_fda_regulatory_intervention) {
      xml += `      <has_dmc>${yesNoToXml(formData.oversight.data_monitoring_fda_regulatory_intervention)}</has_dmc>\n`;
    }

    // irb_info
    if (formData.oversight?.human_subject_review || formData.oversight?.human_subject_protection_status) {
      xml += '      <irb_info>\n';
      if (formData.oversight.human_subject_protection_status) {
        const statusMap = {
          'Submitted-approved': 'Approved',
          'Submitted-Pending': 'Pending',
          'Request Not yet Submitted': 'Not yet submitted',
          'Submitted-Denied': 'Denied',
          'Submission Not Required': 'Not required',
        };
        const status = statusMap[formData.oversight.human_subject_protection_status] || formData.oversight.human_subject_protection_status;
        xml += `        <approval_status>${esc(status)}</approval_status>\n`;
      }
      if (formData.oversight.board_approval_number) {
        xml += `        <approval_number>${esc(formData.oversight.board_approval_number)}</approval_number>\n`;
      }
      if (formData.oversight.board_name) {
        xml += `        <name>${esc(formData.oversight.board_name)}</name>\n`;
      }
      if (formData.oversight.board_affiliation) {
        xml += `        <affiliation>${esc(formData.oversight.board_affiliation)}</affiliation>\n`;
      }
      if (formData.oversight.board_contact) {
        xml += `        <email>${esc(formData.oversight.board_contact)}</email>\n`;
      }
      xml += '      </irb_info>\n';
    }

    xml += '    </oversight_info>\n';
  }

  // is_ind_study and ind_info
  if (formData.oversight?.has_ind_ide === 'Yes') {
    xml += '    <is_ind_study>yes</is_ind_study>\n';
    if (formData.oversight?.ind_ide_number) {
      xml += '    <ind_info>\n';
      xml += '      <ind_grantor>CDER</ind_grantor>\n'; // Default, should be configurable
      xml += `      <ind_number>${esc(formData.oversight.ind_ide_number)}</ind_number>\n`;
      if (formData.oversight.ind_serial_number) {
        xml += `      <ind_serial_number>${esc(formData.oversight.ind_serial_number)}</ind_serial_number>\n`;
      }
      if (formData.oversight.expanded_access) {
        xml += `      <has_expanded_access>${esc(formData.oversight.expanded_access.toLowerCase())}</has_expanded_access>\n`;
      }
      xml += '    </ind_info>\n';
    }
  } else if (formData.oversight?.has_ind_ide === 'No') {
    xml += '    <is_ind_study>no</is_ind_study>\n';
  }

  // brief_summary
  if (formData.study_description?.brief_summary) {
    xml += '    <brief_summary>\n';
    xml += `      <textblock>${esc(formData.study_description.brief_summary)}</textblock>\n`;
    xml += '    </brief_summary>\n';
  }

  // detailed_description
  if (formData.study_description?.detailed_description) {
    xml += '    <detailed_description>\n';
    xml += `      <textblock>${esc(formData.study_description.detailed_description)}</textblock>\n`;
    xml += '    </detailed_description>\n';
  }

  // overall_status
  if (formData.study_status?.overall_recruitment_status) {
    xml += `    <overall_status>${esc(formData.study_status.overall_recruitment_status)}</overall_status>\n`;
  }

  // why_stopped
  if (formData.study_status?.withdrawal_reason) {
    xml += `    <why_stopped>${esc(formData.study_status.withdrawal_reason)}</why_stopped>\n`;
  }

  // verification_date
  if (formData.study_status?.record_verification_date) {
    xml += `    <verification_date>${fmtDate(formData.study_status.record_verification_date)}</verification_date>\n`;
  }

  // start_date and start_date_type
  if (formData.study_status?.study_start_date) {
    xml += `    <start_date>${fmtDate(formData.study_status.study_start_date)}</start_date>\n`;
    xml += '    <start_date_type>Anticipated</start_date_type>\n'; // Default, can be made configurable
  }

  // primary_compl_date
  if (formData.study_status?.primary_completion_date) {
    xml += `    <primary_compl_date>${fmtDate(formData.study_status.primary_completion_date)}</primary_compl_date>\n`;
    xml += '    <primary_compl_date_type>Anticipated</primary_compl_date_type>\n';
  }

  // last_follow_up_date (study completion date)
  if (formData.study_status?.study_completion_date) {
    xml += `    <last_follow_up_date>${fmtDate(formData.study_status.study_completion_date)}</last_follow_up_date>\n`;
    xml += '    <last_follow_up_date_type>Anticipated</last_follow_up_date_type>\n';
  }

  // study_design
  const studyType = formData.study_identification?.study_type || '';
  xml += '    <study_design>\n';
  xml += `      <study_type>${esc(studyType)}</study_type>\n`;

  if (studyType === 'Interventional' && formData.study_design?.interventional) {
    const inter = formData.study_design.interventional;
    xml += '      <interventional_design>\n';
    
    if (inter.primary_purpose) {
      xml += `        <interventional_subtype>${esc(inter.primary_purpose)}</interventional_subtype>\n`;
    }
    
    if (inter.study_phase) {
      xml += `        <phase>${esc(inter.study_phase)}</phase>\n`;
    }

    if (inter.study_model) {
      const modelMap = {
        'Single Group': 'Single Group Assignment',
        'Parallel': 'Parallel Assignment',
        'Cross-over': 'Crossover Assignment',
        'Factorial': 'Factorial Assignment',
        'Sequential': 'Sequential Assignment',
      };
      const mappedModel = modelMap[inter.study_model] || inter.study_model;
      xml += `        <assignment>${esc(mappedModel)}</assignment>\n`;
    }

    if (inter.study_model_description) {
      xml += '        <model_description>\n';
      xml += `          <textblock>${esc(inter.study_model_description)}</textblock>\n`;
      xml += '        </model_description>\n';
    }

    if (inter.allocation) {
      const allocMap = {
        'Randomized': 'Randomized',
        'Non Randomized': 'Non-randomized',
        'N/A': 'N/A',
      };
      xml += `        <allocation>${esc(allocMap[inter.allocation] || inter.allocation)}</allocation>\n`;
    }

    // masking
    if (inter.masking_enabled === 'Yes') {
      xml += '        <no_masking>no</no_masking>\n';
      const roles = inter.masking_roles || {};
      if (roles.participants) xml += '        <masked_subject>yes</masked_subject>\n';
      if (roles.care_providers) xml += '        <masked_caregiver>yes</masked_caregiver>\n';
      if (roles.investigators) xml += '        <masked_investigator>yes</masked_investigator>\n';
      if (roles.outcomes_assessors) xml += '        <masked_assessor>yes</masked_assessor>\n';
      if (inter.masking_description) {
        xml += '        <masking_description>\n';
        xml += `          <textblock>${esc(inter.masking_description)}</textblock>\n`;
        xml += '        </masking_description>\n';
      }
    } else if (inter.masking_enabled === 'No') {
      xml += '        <no_masking>yes</no_masking>\n';
    }

    if (inter.number_of_arms) {
      xml += `        <number_of_arms>${esc(String(inter.number_of_arms))}</number_of_arms>\n`;
    }

    xml += '      </interventional_design>\n';
  } else if (studyType === 'Observational' && formData.study_design?.observational) {
    const obs = formData.study_design.observational;
    xml += '      <observational_design>\n';
    
    if (obs.study_model) {
      xml += `        <observational_study_design>${esc(obs.study_model)}</observational_study_design>\n`;
    }
    
    if (obs.biospecimen_retention) {
      xml += `        <biospecimen_retention>${esc(obs.biospecimen_retention)}</biospecimen_retention>\n`;
    }
    
    if (obs.biospecimen_description) {
      xml += '        <biospecimen_description>\n';
      xml += `          <textblock>${esc(obs.biospecimen_description)}</textblock>\n`;
      xml += '        </biospecimen_description>\n';
    }
    
    if (obs.time_perspective) {
      xml += `        <timing>${esc(obs.time_perspective)}</timing>\n`;
    }
    
    if (obs.number_of_groups) {
      xml += `        <number_of_groups>${esc(String(obs.number_of_groups))}</number_of_groups>\n`;
    }
    
    xml += '      </observational_design>\n';
  }

  xml += '    </study_design>\n';

  // primary_outcome
  (formData.outcomes?.primary || []).forEach(outcome => {
    if (outcome.title) {
      xml += '    <primary_outcome>\n';
      xml += `      <outcome_measure>${esc(outcome.title)}</outcome_measure>\n`;
      if (outcome.timeframe) {
        xml += `      <outcome_time_frame>${esc(outcome.timeframe)}</outcome_time_frame>\n`;
      }
      if (outcome.description) {
        xml += '      <outcome_description>\n';
        xml += `        <textblock>${esc(outcome.description)}</textblock>\n`;
        xml += '      </outcome_description>\n';
      }
      xml += '    </primary_outcome>\n';
    }
  });

  // secondary_outcome
  (formData.outcomes?.secondary || []).forEach(outcome => {
    if (outcome.title) {
      xml += '    <secondary_outcome>\n';
      xml += `      <outcome_measure>${esc(outcome.title)}</outcome_measure>\n`;
      if (outcome.timeframe) {
        xml += `      <outcome_time_frame>${esc(outcome.timeframe)}</outcome_time_frame>\n`;
      }
      if (outcome.description) {
        xml += '      <outcome_description>\n';
        xml += `        <textblock>${esc(outcome.description)}</textblock>\n`;
        xml += '      </outcome_description>\n';
      }
      xml += '    </secondary_outcome>\n';
    }
  });

  // other_outcome
  (formData.outcomes?.other || []).forEach(outcome => {
    if (outcome.title) {
      xml += '    <other_outcome>\n';
      xml += `      <outcome_measure>${esc(outcome.title)}</outcome_measure>\n`;
      if (outcome.timeframe) {
        xml += `      <outcome_time_frame>${esc(outcome.timeframe)}</outcome_time_frame>\n`;
      }
      if (outcome.description) {
        xml += '      <outcome_description>\n';
        xml += `        <textblock>${esc(outcome.description)}</textblock>\n`;
        xml += '      </outcome_description>\n';
      }
      xml += '    </other_outcome>\n';
    }
  });

  // enrollment
  const enrollment = formData.study_design?.interventional?.enrollment || formData.study_design?.observational?.enrollment;
  if (enrollment) {
    xml += `    <enrollment>${esc(String(enrollment))}</enrollment>\n`;
    xml += '    <enrollment_type>Anticipated</enrollment_type>\n';
  }

  // condition
  if (formData.condition_keywords?.primary_condition) {
    xml += `    <condition>${esc(formData.condition_keywords.primary_condition)}</condition>\n`;
  }

  // keyword
  const keywords = splitList(formData.condition_keywords?.keywords || '');
  keywords.forEach(keyword => {
    if (keyword) {
      xml += `    <keyword>${esc(keyword)}</keyword>\n`;
    }
  });

  // arm_group
  (formData.arms_groups_interventions?.arms || []).forEach(arm => {
    if (arm.title) {
      xml += '    <arm_group>\n';
      xml += `      <arm_group_label>${esc(arm.title)}</arm_group_label>\n`;
      if (arm.type) {
        xml += `      <arm_type>${esc(arm.type)}</arm_type>\n`;
      }
      if (arm.description) {
        xml += '      <arm_group_description>\n';
        xml += `        <textblock>${esc(arm.description)}</textblock>\n`;
        xml += '      </arm_group_description>\n';
      }
      xml += '    </arm_group>\n';
    }
  });

  // intervention
  (formData.arms_groups_interventions?.interventions || []).forEach(intervention => {
    if (intervention.name) {
      xml += '    <intervention>\n';
      if (intervention.intervention_type) {
        xml += `      <intervention_type>${esc(intervention.intervention_type)}</intervention_type>\n`;
      }
      xml += `      <intervention_name>${esc(intervention.name)}</intervention_name>\n`;
      if (intervention.description) {
        xml += '      <intervention_description>\n';
        xml += `        <textblock>${esc(intervention.description)}</textblock>\n`;
        xml += '      </intervention_description>\n';
      }
      if (intervention.arm_group_cross_reference) {
        const armRefs = splitList(intervention.arm_group_cross_reference);
        armRefs.forEach(ref => {
          if (ref) xml += `      <arm_group_label>${esc(ref)}</arm_group_label>\n`;
        });
      }
      xml += '    </intervention>\n';
    }
  });

  // eligibility
  if (formData.eligibility) {
    xml += '    <eligibility>\n';
    
    if (studyType === 'Observational' && formData.eligibility.study_population_description) {
      xml += '      <study_population>\n';
      xml += `        <textblock>${esc(formData.eligibility.study_population_description)}</textblock>\n`;
      xml += '      </study_population>\n';
    }
    
    if (studyType === 'Observational' && formData.eligibility.sampling_method) {
      xml += `      <sampling_method>${esc(formData.eligibility.sampling_method)}</sampling_method>\n`;
    }
    
    if (formData.eligibility.eligibility_criteria) {
      xml += '      <criteria>\n';
      xml += `        <textblock>${esc(formData.eligibility.eligibility_criteria)}</textblock>\n`;
      xml += '      </criteria>\n';
    }
    
    if (formData.eligibility.healthy_volunteers) {
      xml += `      <healthy_volunteers>${yesNoToXml(formData.eligibility.healthy_volunteers)}</healthy_volunteers>\n`;
    }
    
    if (formData.eligibility.sex) {
      const genderMap = {
        'All': 'All',
        'Both': 'All',
        'Male': 'Male',
        'Female': 'Female',
      };
      xml += `      <gender>${esc(genderMap[formData.eligibility.sex] || formData.eligibility.sex)}</gender>\n`;
    }
    
    if (formData.eligibility.gender_based) {
      xml += `      <gender_based>${yesNoToXml(formData.eligibility.gender_based)}</gender_based>\n`;
    }
    
    if (formData.eligibility.gender_description) {
      xml += '      <gender_description>\n';
      xml += `        <textblock>${esc(formData.eligibility.gender_description)}</textblock>\n`;
      xml += '      </gender_description>\n';
    }
    
    const minAge = formData.eligibility.age_min ? `${formData.eligibility.age_min} ${formData.eligibility.age_min_unit || 'Years'}` : 'N/A';
    const maxAge = formData.eligibility.age_max ? `${formData.eligibility.age_max} ${formData.eligibility.age_max_unit || 'Years'}` : 'N/A';
    xml += `      <minimum_age>${esc(minAge)}</minimum_age>\n`;
    xml += `      <maximum_age>${esc(maxAge)}</maximum_age>\n`;
    
    xml += '    </eligibility>\n';
  }

  // overall_official
  (formData.contacts_locations_investigators?.study_officials || []).forEach(official => {
    if (official.last_name) {
      xml += '    <overall_official>\n';
      if (official.first_name) xml += `      <first_name>${esc(official.first_name)}</first_name>\n`;
      if (official.middle_initials) xml += `      <middle_name>${esc(official.middle_initials)}</middle_name>\n`;
      xml += `      <last_name>${esc(official.last_name)}</last_name>\n`;
      if (official.degree) xml += `      <degrees>${esc(official.degree)}</degrees>\n`;
      if (official.role) xml += `      <role>${esc(official.role)}</role>\n`;
      if (official.affiliation) xml += `      <affiliation>${esc(official.affiliation)}</affiliation>\n`;
      xml += '    </overall_official>\n';
    }
  });

  // overall_contact
  if (formData.contacts_locations_investigators?.central_contact) {
    const contact = formData.contacts_locations_investigators.central_contact;
    if (contact.last_name) {
      xml += '    <overall_contact>\n';
      if (contact.first_name) xml += `      <first_name>${esc(contact.first_name)}</first_name>\n`;
      if (contact.middle_initials) xml += `      <middle_name>${esc(contact.middle_initials)}</middle_name>\n`;
      xml += `      <last_name>${esc(contact.last_name)}</last_name>\n`;
      if (contact.degree) xml += `      <degrees>${esc(contact.degree)}</degrees>\n`;
      if (contact.phone) xml += `      <phone>${esc(contact.phone)}</phone>\n`;
      if (contact.phone_ext) xml += `      <phone_ext>${esc(contact.phone_ext)}</phone_ext>\n`;
      if (contact.email) xml += `      <email>${esc(contact.email)}</email>\n`;
      xml += '    </overall_contact>\n';
    }
  }

  // overall_contact_backup
  if (formData.contacts_locations_investigators?.central_contact_backup) {
    const contact = formData.contacts_locations_investigators.central_contact_backup;
    if (contact.last_name) {
      xml += '    <overall_contact_backup>\n';
      if (contact.first_name) xml += `      <first_name>${esc(contact.first_name)}</first_name>\n`;
      if (contact.middle_initials) xml += `      <middle_name>${esc(contact.middle_initials)}</middle_name>\n`;
      xml += `      <last_name>${esc(contact.last_name)}</last_name>\n`;
      if (contact.degree) xml += `      <degrees>${esc(contact.degree)}</degrees>\n`;
      if (contact.phone) xml += `      <phone>${esc(contact.phone)}</phone>\n`;
      if (contact.phone_ext) xml += `      <phone_ext>${esc(contact.phone_ext)}</phone_ext>\n`;
      if (contact.email) xml += `      <email>${esc(contact.email)}</email>\n`;
      xml += '    </overall_contact_backup>\n';
    }
  }

  // location
  (formData.contacts_locations_investigators?.facilities || []).forEach(facility => {
    if (facility.name) {
      xml += '    <location>\n';
      xml += '      <facility>\n';
      xml += `        <name>${esc(facility.name)}</name>\n`;
      xml += '        <address>\n';
      if (facility.city) xml += `          <city>${esc(facility.city)}</city>\n`;
      if (facility.state) xml += `          <state>${esc(facility.state)}</state>\n`;
      if (facility.postal_code) xml += `          <zip>${esc(facility.postal_code)}</zip>\n`;
      if (facility.country) xml += `          <country>${esc(facility.country)}</country>\n`;
      xml += '        </address>\n';
      xml += '      </facility>\n';
      
      if (facility.individual_site_status) {
        xml += `      <status>${esc(facility.individual_site_status)}</status>\n`;
      }
      
      if (facility.contact?.last_name) {
        xml += '      <contact>\n';
        if (facility.contact.first_name) xml += `        <first_name>${esc(facility.contact.first_name)}</first_name>\n`;
        if (facility.contact.middle_initials) xml += `        <middle_name>${esc(facility.contact.middle_initials)}</middle_name>\n`;
        xml += `        <last_name>${esc(facility.contact.last_name)}</last_name>\n`;
        if (facility.contact.degree) xml += `        <degrees>${esc(facility.contact.degree)}</degrees>\n`;
        if (facility.contact.phone) xml += `        <phone>${esc(facility.contact.phone)}</phone>\n`;
        if (facility.contact.phone_ext) xml += `        <phone_ext>${esc(facility.contact.phone_ext)}</phone_ext>\n`;
        if (facility.contact.email) xml += `        <email>${esc(facility.contact.email)}</email>\n`;
        xml += '      </contact>\n';
      }
      
      if (facility.contact_backup?.last_name) {
        xml += '      <contact_backup>\n';
        if (facility.contact_backup.first_name) xml += `        <first_name>${esc(facility.contact_backup.first_name)}</first_name>\n`;
        if (facility.contact_backup.middle_initials) xml += `        <middle_name>${esc(facility.contact_backup.middle_initials)}</middle_name>\n`;
        xml += `        <last_name>${esc(facility.contact_backup.last_name)}</last_name>\n`;
        if (facility.contact_backup.degree) xml += `        <degrees>${esc(facility.contact_backup.degree)}</degrees>\n`;
        if (facility.contact_backup.phone) xml += `        <phone>${esc(facility.contact_backup.phone)}</phone>\n`;
        if (facility.contact_backup.phone_ext) xml += `        <phone_ext>${esc(facility.contact_backup.phone_ext)}</phone_ext>\n`;
        if (facility.contact_backup.email) xml += `        <email>${esc(facility.contact_backup.email)}</email>\n`;
        xml += '      </contact_backup>\n';
      }
      
      (facility.investigators || []).forEach(inv => {
        if (inv.last_name) {
          xml += '      <investigator>\n';
          if (inv.first_name) xml += `        <first_name>${esc(inv.first_name)}</first_name>\n`;
          if (inv.middle_initials) xml += `        <middle_name>${esc(inv.middle_initials)}</middle_name>\n`;
          xml += `        <last_name>${esc(inv.last_name)}</last_name>\n`;
          if (inv.degree) xml += `        <degrees>${esc(inv.degree)}</degrees>\n`;
          if (inv.role) xml += `        <role>${esc(inv.role)}</role>\n`;
          xml += '      </investigator>\n';
        }
      });
      
      xml += '    </location>\n';
    }
  });

  // ipd_sharing_statement
  if (formData.ipd_sharing?.plan) {
    xml += '    <ipd_sharing_statement>\n';
    if (formData.ipd_sharing.plan) {
      const planMap = {
        'Yes': 'yes',
        'No': 'no',
        'Undecided': 'undecided',
      };
      xml += `      <sharing_ipd>${planMap[formData.ipd_sharing.plan] || formData.ipd_sharing.plan.toLowerCase()}</sharing_ipd>\n`;
    }
    if (formData.ipd_sharing.description) {
      xml += '      <ipd_description>\n';
      xml += `        <textblock>${esc(formData.ipd_sharing.description)}</textblock>\n`;
      xml += '      </ipd_description>\n';
    }
    
    const supportingInfoTypes = splitList(formData.ipd_sharing.supporting_info_types || '');
    supportingInfoTypes.forEach(type => {
      if (type.includes('Protocol')) xml += '      <ipd_info_type_protocol>yes</ipd_info_type_protocol>\n';
      if (type.includes('SAP') || type.includes('Statistical Analysis Plan')) xml += '      <ipd_info_type_sap>yes</ipd_info_type_sap>\n';
      if (type.includes('ICF') || type.includes('Informed Consent')) xml += '      <ipd_info_type_icf>yes</ipd_info_type_icf>\n';
      if (type.includes('CSR') || type.includes('Clinical Study Report')) xml += '      <ipd_info_type_csr>yes</ipd_info_type_csr>\n';
      if (type.includes('Analytic Code')) xml += '      <ipd_info_type_analytic_code>yes</ipd_info_type_analytic_code>\n';
    });
    
    if (formData.ipd_sharing.timeframe) {
      xml += '      <ipd_time_frame>\n';
      xml += `        <textblock>${esc(formData.ipd_sharing.timeframe)}</textblock>\n`;
      xml += '      </ipd_time_frame>\n';
    }
    
    if (formData.ipd_sharing.access_criteria) {
      xml += '      <ipd_access_criteria>\n';
      xml += `        <textblock>${esc(formData.ipd_sharing.access_criteria)}</textblock>\n`;
      xml += '      </ipd_access_criteria>\n';
    }
    
    if (formData.ipd_sharing.url) {
      xml += `      <ipd_url>${esc(formData.ipd_sharing.url)}</ipd_url>\n`;
    }
    
    xml += '    </ipd_sharing_statement>\n';
  }

  // link
  (formData.references?.links || []).forEach(link => {
    if (link.url) {
      xml += '    <link>\n';
      xml += `      <url>${esc(link.url)}</url>\n`;
      xml += `      <description>${esc(link.description || '')}</description>\n`;
      xml += '    </link>\n';
    }
  });

  // reference
  (formData.references?.citations || []).forEach(ref => {
    if (ref.citation || ref.pubmed_id) {
      xml += '    <reference>\n';
      if (ref.citation) xml += `      <citation>${esc(ref.citation)}</citation>\n`;
      if (ref.pubmed_id) xml += `      <pubmed_id>${esc(ref.pubmed_id)}</pubmed_id>\n`;
      if (ref.result_reference === 'Yes') xml += '      <is_results_ref>yes</is_results_ref>\n';
      xml += '    </reference>\n';
    }
  });

  // study_document
  (formData.references?.available_ipd_information || []).forEach(doc => {
    if (doc.info_type) {
      xml += '    <study_document>\n';
      xml += `      <study_document_type>${esc(doc.info_type)}</study_document_type>\n`;
      if (doc.url) xml += `      <url>${esc(doc.url)}</url>\n`;
      if (doc.identifier) xml += `      <study_document_identifier>${esc(doc.identifier)}</study_document_identifier>\n`;
      if (doc.comment) {
        xml += '      <study_document_comments>\n';
        xml += `        <textblock>${esc(doc.comment)}</textblock>\n`;
        xml += '      </study_document_comments>\n';
      }
      xml += '    </study_document>\n';
    }
  });

  xml += '  </clinical_study>\n';
  xml += '</study_collection>';

  return xml;
}

const ClinicalTrialsGov = ({ projectId }) => {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await protocolRegistrationService.getByProject(projectId);
        setFormData({
          ...initialForm,
          study_identification: {
            unique_protocol_id: data?.study_identification?.unique_protocol_id || '',
            brief_title: data?.study_identification?.brief_title || '',
            official_title: data?.study_identification?.official_title || '',
            secondary_id_type: data?.study_identification?.secondary_id?.id_type || '',
            secondary_id_description: data?.study_identification?.secondary_id?.description || '',
            study_type: data?.study_identification?.study_type || '',
          },
          study_status: {
            record_verification_date: formatDate(data?.study_status?.record_verification_date),
            overall_recruitment_status: data?.study_status?.overall_recruitment_status || '',
            withdrawal_reason: data?.study_status?.withdrawal_reason || '',
            study_start_date: formatDate(data?.study_status?.study_start_date),
            primary_completion_date: formatDate(data?.study_status?.primary_completion_date),
            study_completion_date: formatDate(data?.study_status?.study_completion_date),
          },
          sponsors_collaborators: {
            investigator_name: data?.sponsors_collaborators?.responsible_party?.investigator_name || '',
            investigator_title: data?.sponsors_collaborators?.responsible_party?.official_title || '',
            investigator_affiliation: data?.sponsors_collaborators?.responsible_party?.affiliation || '',
            sponsor_name: data?.sponsors_collaborators?.sponsor_name || '',
            collaborators: (data?.sponsors_collaborators?.collaborators || []).join(', '),
          },
          oversight: {
            fda_regulated_drug_product: formatYesNo(data?.oversight?.fda_regulated_drug_product),
            fda_regulated_device_product: formatYesNo(data?.oversight?.fda_regulated_device_product),
            has_ind_ide: formatYesNo(data?.oversight?.ind_ide?.has_ind_ide),
            ind_ide_number: data?.oversight?.ind_ide?.ind_ide_number || '',
            ind_serial_number: data?.oversight?.ind_ide?.ind_serial_number || '',
            expanded_access: data?.oversight?.ind_ide?.expanded_access || '',
            product_manufactured_exported_us: formatYesNo(data?.oversight?.product_manufactured_exported_us),
            human_subject_review: data?.oversight?.human_subject_review || '',
            human_subject_protection_status: data?.oversight?.human_subject_protection_status || '',
            board_approval_number: data?.oversight?.board_approval_number || '',
            board_name: data?.oversight?.board_name || '',
            board_affiliation: data?.oversight?.board_affiliation || '',
            board_contact: data?.oversight?.board_contact || '',
            data_monitoring_fda_regulatory_intervention: formatYesNo(data?.oversight?.data_monitoring_committee?.fda_regulatory_intervention),
            data_monitoring_section801_clinical_trials: formatYesNo(data?.oversight?.data_monitoring_committee?.section801_clinical_trials),
          },
          study_description: {
            brief_summary: data?.study_description?.brief_summary || '',
            detailed_description: data?.study_description?.detailed_description || '',
          },
          condition_keywords: {
            primary_condition: data?.condition_keywords?.primary_condition || '',
            keywords: (data?.condition_keywords?.keywords || []).join(', '),
          },
          study_design: {
            interventional: {
              primary_purpose: data?.study_design?.interventional?.primary_purpose || '',
              study_phase: data?.study_design?.interventional?.study_phase || '',
              study_model: data?.study_design?.interventional?.study_model || '',
              study_model_description: data?.study_design?.interventional?.study_model_description || '',
              number_of_arms: data?.study_design?.interventional?.number_of_arms ?? '',
              masking_enabled: formatYesNo(data?.study_design?.interventional?.masking?.enabled),
              masking_roles: {
                participants: data?.study_design?.interventional?.masking?.roles?.participants || false,
                care_providers: data?.study_design?.interventional?.masking?.roles?.care_providers || false,
                investigators: data?.study_design?.interventional?.masking?.roles?.investigators || false,
                outcomes_assessors: data?.study_design?.interventional?.masking?.roles?.outcomes_assessors || false,
              },
              masking_description: data?.study_design?.interventional?.masking?.description || '',
              allocation: data?.study_design?.interventional?.allocation || '',
              enrollment: data?.study_design?.interventional?.enrollment ?? '',
            },
            observational: {
              study_model: data?.study_design?.observational?.study_model || '',
              time_perspective: data?.study_design?.observational?.time_perspective || '',
              biospecimen_retention: data?.study_design?.observational?.biospecimen_retention || '',
              biospecimen_description: data?.study_design?.observational?.biospecimen_description || '',
              enrollment: data?.study_design?.observational?.enrollment ?? '',
              target_followup_duration: data?.study_design?.observational?.target_followup_duration || '',
              number_of_groups: data?.study_design?.observational?.number_of_groups ?? '',
            },
          },
          arms_groups_interventions: {
            arms: (data?.arms_groups_interventions?.arms || []).length
              ? data.arms_groups_interventions.arms
              : [...initialForm.arms_groups_interventions.arms],
            groups: (data?.arms_groups_interventions?.groups || []).length
              ? data.arms_groups_interventions.groups
              : [...initialForm.arms_groups_interventions.groups],
            interventions: (data?.arms_groups_interventions?.interventions || []).length
              ? data.arms_groups_interventions.interventions
              : [...initialForm.arms_groups_interventions.interventions],
          },
          outcomes: {
            primary: (data?.outcomes?.primary || []).length ? data.outcomes.primary : [...initialForm.outcomes.primary],
            secondary: (data?.outcomes?.secondary || []).length ? data.outcomes.secondary : [...initialForm.outcomes.secondary],
            other: (data?.outcomes?.other || []).length ? data.outcomes.other : [...initialForm.outcomes.other],
          },
          eligibility: {
            sex: data?.eligibility?.sex || '',
            gender_based: formatYesNo(data?.eligibility?.gender_based),
            gender_description: data?.eligibility?.gender_description || '',
            age_min: data?.eligibility?.age_min ?? '',
            age_min_unit: data?.eligibility?.age_min_unit || '',
            age_max: data?.eligibility?.age_max ?? '',
            age_max_unit: data?.eligibility?.age_max_unit || '',
            healthy_volunteers: formatYesNo(data?.eligibility?.healthy_volunteers),
            eligibility_criteria: data?.eligibility?.eligibility_criteria || '',
            study_population_description: data?.eligibility?.study_population_description || '',
            sampling_method: data?.eligibility?.sampling_method || '',
          },
          contacts_locations_investigators: {
            central_contact: {
              first_name: data?.contacts_locations_investigators?.central_contact?.first_name || '',
              middle_initials: data?.contacts_locations_investigators?.central_contact?.middle_initials || '',
              last_name: data?.contacts_locations_investigators?.central_contact?.last_name || '',
              degree: data?.contacts_locations_investigators?.central_contact?.degree || '',
              phone: data?.contacts_locations_investigators?.central_contact?.phone || '',
              phone_ext: data?.contacts_locations_investigators?.central_contact?.phone_ext || '',
              email: data?.contacts_locations_investigators?.central_contact?.email || '',
            },
            central_contact_backup: {
              first_name: data?.contacts_locations_investigators?.central_contact_backup?.first_name || '',
              middle_initials: data?.contacts_locations_investigators?.central_contact_backup?.middle_initials || '',
              last_name: data?.contacts_locations_investigators?.central_contact_backup?.last_name || '',
              degree: data?.contacts_locations_investigators?.central_contact_backup?.degree || '',
              phone: data?.contacts_locations_investigators?.central_contact_backup?.phone || '',
              phone_ext: data?.contacts_locations_investigators?.central_contact_backup?.phone_ext || '',
              email: data?.contacts_locations_investigators?.central_contact_backup?.email || '',
            },
            study_officials: (data?.contacts_locations_investigators?.study_officials || []).length
              ? data.contacts_locations_investigators.study_officials
              : [...initialForm.contacts_locations_investigators.study_officials],
            facilities: (data?.contacts_locations_investigators?.facilities || []).length
              ? data.contacts_locations_investigators.facilities
              : [...initialForm.contacts_locations_investigators.facilities],
          },
          ipd_sharing: {
            plan: data?.ipd_sharing?.plan || '',
            description: data?.ipd_sharing?.description || '',
            supporting_info_types: (data?.ipd_sharing?.supporting_info_types || []).join(', '),
            timeframe: data?.ipd_sharing?.timeframe || '',
            access_criteria: data?.ipd_sharing?.access_criteria || '',
            url: data?.ipd_sharing?.url || '',
            reason_if_no: data?.ipd_sharing?.reason_if_no || '',
          },
          references: {
            citations: (data?.references?.citations || []).length
              ? data.references.citations
              : [...initialForm.references.citations],
            links: (data?.references?.links || []).length
              ? data.references.links
              : [...initialForm.references.links],
            available_ipd_information: (data?.references?.available_ipd_information || []).length
              ? data.references.available_ipd_information
              : [...initialForm.references.available_ipd_information],
          },
        });
      } catch (err) {
        if (err?.response?.status !== 404) {
          setError(err?.message || 'Failed to load protocol registration');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  const handleIdentificationChange = (field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      study_identification: {
        ...prev.study_identification,
        [field]: value,
      },
    }));
  };

  const handleStatusChange = (field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      study_status: {
        ...prev.study_status,
        [field]: value,
      },
    }));
  };

  const handleSponsorsChange = (field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      sponsors_collaborators: {
        ...prev.sponsors_collaborators,
        [field]: value,
      },
    }));
  };

  const handleOversightChange = (field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      oversight: {
        ...prev.oversight,
        [field]: value,
      },
    }));
  };

  const handleStudyDescriptionChange = (field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      study_description: {
        ...prev.study_description,
        [field]: value,
      },
    }));
  };

  const handleConditionChange = (field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      condition_keywords: {
        ...prev.condition_keywords,
        [field]: value,
      },
    }));
  };

  const handleArmsGroupsChange = (section, index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const updatedSection = prev.arms_groups_interventions[section].map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      );
      return {
        ...prev,
        arms_groups_interventions: {
          ...prev.arms_groups_interventions,
          [section]: updatedSection,
        },
      };
    });
  };

  const addArmsGroupsRow = (section, emptyRow) => () => {
    setFormData((prev) => ({
      ...prev,
      arms_groups_interventions: {
        ...prev.arms_groups_interventions,
        [section]: [...prev.arms_groups_interventions[section], emptyRow],
      },
    }));
  };

  const handleOutcomeChange = (section, index, field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => {
      const updated = prev.outcomes[section].map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      );
      return {
        ...prev,
        outcomes: { ...prev.outcomes, [section]: updated },
      };
    });
  };

  const addOutcomeRow = (section) => () => {
    setFormData((prev) => ({
      ...prev,
      outcomes: {
        ...prev.outcomes,
        [section]: [...prev.outcomes[section], { title: '', description: '', timeframe: '' }],
      },
    }));
  };

  const handleEligibilityChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      eligibility: {
        ...prev.eligibility,
        [field]: value,
      },
    }));
  };

  const handleContactChange = (section, field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      contacts_locations_investigators: {
        ...prev.contacts_locations_investigators,
        [section]: {
          ...prev.contacts_locations_investigators[section],
          [field]: value,
        },
      },
    }));
  };

  const handleStudyOfficialChange = (index, field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => {
      const updated = prev.contacts_locations_investigators.study_officials.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      );
      return {
        ...prev,
        contacts_locations_investigators: {
          ...prev.contacts_locations_investigators,
          study_officials: updated,
        },
      };
    });
  };

  const addStudyOfficialRow = () => {
    setFormData((prev) => ({
      ...prev,
      contacts_locations_investigators: {
        ...prev.contacts_locations_investigators,
        study_officials: [
          ...prev.contacts_locations_investigators.study_officials,
          { first_name: '', middle_initials: '', last_name: '', degree: '', affiliation: '', role: '' },
        ],
      },
    }));
  };

  const handleFacilityChange = (index, field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => {
      const facilities = prev.contacts_locations_investigators.facilities.map((f, idx) =>
        idx === index ? { ...f, [field]: value } : f
      );
      return {
        ...prev,
        contacts_locations_investigators: { ...prev.contacts_locations_investigators, facilities },
      };
    });
  };

  const handleFacilityContactChange = (facilityIndex, contactField, field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => {
      const facilities = prev.contacts_locations_investigators.facilities.map((f, idx) => {
        if (idx !== facilityIndex) return f;
        return {
          ...f,
          [contactField]: {
            ...f[contactField],
            [field]: value,
          },
        };
      });
      return {
        ...prev,
        contacts_locations_investigators: { ...prev.contacts_locations_investigators, facilities },
      };
    });
  };

  const handleFacilityInvestigatorChange = (facilityIndex, invIndex, field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => {
      const facilities = prev.contacts_locations_investigators.facilities.map((f, idx) => {
        if (idx !== facilityIndex) return f;
        const investigators = f.investigators.map((inv, i) =>
          i === invIndex ? { ...inv, [field]: value } : inv
        );
        return { ...f, investigators };
      });
      return {
        ...prev,
        contacts_locations_investigators: { ...prev.contacts_locations_investigators, facilities },
      };
    });
  };

  const addFacilityRow = () => {
    setFormData((prev) => ({
      ...prev,
      contacts_locations_investigators: {
        ...prev.contacts_locations_investigators,
        facilities: [
          ...prev.contacts_locations_investigators.facilities,
          {
            name: '',
            city: '',
            state: '',
            postal_code: '',
            country: '',
            individual_site_status: '',
            contact: { first_name: '', middle_initials: '', last_name: '', degree: '', phone: '', phone_ext: '', email: '' },
            contact_backup: { first_name: '', middle_initials: '', last_name: '', degree: '', phone: '', phone_ext: '', email: '' },
            investigators: [{ first_name: '', middle_initials: '', last_name: '', degree: '', role: '' }],
          },
        ],
      },
    }));
  };

  const addFacilityInvestigatorRow = (facilityIndex) => () => {
    setFormData((prev) => {
      const facilities = prev.contacts_locations_investigators.facilities.map((f, idx) => {
        if (idx !== facilityIndex) return f;
        return {
          ...f,
          investigators: [...f.investigators, { first_name: '', middle_initials: '', last_name: '', degree: '', role: '' }],
        };
      });
      return {
        ...prev,
        contacts_locations_investigators: { ...prev.contacts_locations_investigators, facilities },
      };
    });
  };

  const handleIpdChange = (field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      ipd_sharing: { ...prev.ipd_sharing, [field]: value },
    }));
  };

  const handleReferenceChange = (section, index, field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => {
      const updated = prev.references[section].map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      );
      return { ...prev, references: { ...prev.references, [section]: updated } };
    });
  };

  const addReferenceRow = (section, emptyRow) => () => {
    setFormData((prev) => ({
      ...prev,
      references: {
        ...prev.references,
        [section]: [...prev.references[section], emptyRow],
      },
    }));
  };


  const handleStudyDesignChange = (group, field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      study_design: {
        ...prev.study_design,
        [group]: {
          ...prev.study_design[group],
          [field]: value,
        },
      },
    }));
  };

  const handleMaskingRoleChange = (role) => (event) => {
    const { checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      study_design: {
        ...prev.study_design,
        interventional: {
          ...prev.study_design.interventional,
          masking_roles: {
            ...prev.study_design.interventional.masking_roles,
            [role]: checked,
          },
        },
      },
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!projectId) {
      setError('Please select a project before saving.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await protocolRegistrationService.upsertByProject(projectId, normalizePayload(formData));
      setSuccess(true);
    } catch (err) {
      setError(err?.message || 'Failed to save protocol registration');
    } finally {
      setSaving(false);
    }
  };

  const handleAutoPopulateFromCoreDatasheet = async () => {
    if (!projectId) {
      showSnackbar('Project ID is required', 'error');
      return;
    }

    setAutoPopulating(true);
    try {
      const response = await protocolRegistrationService.autoPopulateRegistry(
        projectId,
        'clinicaltrialsgov'
      );

      if (response.success && response.data) {
        // Merge auto-populated data with existing form data
        const mergedData = { ...formData };
        
        // Deep merge function for nested objects
        const deepMerge = (target, source) => {
          Object.keys(source).forEach(key => {
            if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
              if (!target[key]) target[key] = {};
              deepMerge(target[key], source[key]);
            } else if (source[key] !== null && source[key] !== undefined && source[key] !== '') {
              if (Array.isArray(source[key]) && source[key].length > 0) {
                target[key] = source[key];
              } else if (!Array.isArray(source[key])) {
                target[key] = source[key];
              }
            }
          });
        };

        deepMerge(mergedData, response.data);
        setFormData(mergedData);
        showSnackbar('Form auto-populated from Core Datasheet. Review and save when ready.', 'success');
      } else {
        showSnackbar(response.message || 'Failed to auto-populate from Core Datasheet', 'error');
      }
    } catch (error) {
      console.error('Error auto-populating from Core Datasheet:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to auto-populate from Core Datasheet',
        'error'
      );
    } finally {
      setAutoPopulating(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAutoFill = async () => {
    if (!projectId) {
      setError('Please select a project before auto-fill.');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const data = await protocolRegistrationService.autoFill(projectId);
      
      // Deep merge AI-extracted data with current form data
      setFormData((prev) => ({
        ...prev,
        study_identification: {
          ...prev.study_identification,
          unique_protocol_id: data?.study_identification?.unique_protocol_id || prev.study_identification.unique_protocol_id,
          brief_title: data?.study_identification?.brief_title || prev.study_identification.brief_title,
          official_title: data?.study_identification?.official_title || prev.study_identification.official_title,
          secondary_id_type: data?.study_identification?.secondary_id?.id_type || prev.study_identification.secondary_id_type,
          secondary_id_description: data?.study_identification?.secondary_id?.description || prev.study_identification.secondary_id_description,
          study_type: data?.study_identification?.study_type || prev.study_identification.study_type,
        },
        study_status: {
          ...prev.study_status,
          record_verification_date: data?.study_status?.record_verification_date ? formatDate(data.study_status.record_verification_date) : prev.study_status.record_verification_date,
          overall_recruitment_status: data?.study_status?.overall_recruitment_status || prev.study_status.overall_recruitment_status,
          withdrawal_reason: data?.study_status?.withdrawal_reason || prev.study_status.withdrawal_reason,
          study_start_date: data?.study_status?.study_start_date ? formatDate(data.study_status.study_start_date) : prev.study_status.study_start_date,
          primary_completion_date: data?.study_status?.primary_completion_date ? formatDate(data.study_status.primary_completion_date) : prev.study_status.primary_completion_date,
          study_completion_date: data?.study_status?.study_completion_date ? formatDate(data.study_status.study_completion_date) : prev.study_status.study_completion_date,
        },
        sponsors_collaborators: {
          ...prev.sponsors_collaborators,
          investigator_name: data?.sponsors_collaborators?.responsible_party?.investigator_name || prev.sponsors_collaborators.investigator_name,
          investigator_title: data?.sponsors_collaborators?.responsible_party?.official_title || prev.sponsors_collaborators.investigator_title,
          investigator_affiliation: data?.sponsors_collaborators?.responsible_party?.affiliation || prev.sponsors_collaborators.investigator_affiliation,
          sponsor_name: data?.sponsors_collaborators?.sponsor_name || prev.sponsors_collaborators.sponsor_name,
          collaborators: Array.isArray(data?.sponsors_collaborators?.collaborators) 
            ? data.sponsors_collaborators.collaborators.join(', ') 
            : prev.sponsors_collaborators.collaborators,
        },
        oversight: {
          ...prev.oversight,
          fda_regulated_drug_product: formatYesNo(data?.oversight?.fda_regulated_drug_product) || prev.oversight.fda_regulated_drug_product,
          fda_regulated_device_product: formatYesNo(data?.oversight?.fda_regulated_device_product) || prev.oversight.fda_regulated_device_product,
          has_ind_ide: formatYesNo(data?.oversight?.ind_ide?.has_ind_ide) || prev.oversight.has_ind_ide,
          ind_ide_number: data?.oversight?.ind_ide?.ind_ide_number || prev.oversight.ind_ide_number,
          ind_serial_number: data?.oversight?.ind_ide?.ind_serial_number || prev.oversight.ind_serial_number,
          expanded_access: data?.oversight?.ind_ide?.expanded_access || prev.oversight.expanded_access,
          product_manufactured_exported_us: formatYesNo(data?.oversight?.product_manufactured_exported_us) || prev.oversight.product_manufactured_exported_us,
          human_subject_review: data?.oversight?.human_subject_review || prev.oversight.human_subject_review,
          human_subject_protection_status: data?.oversight?.human_subject_protection_status || prev.oversight.human_subject_protection_status,
          board_approval_number: data?.oversight?.board_approval_number || prev.oversight.board_approval_number,
          board_name: data?.oversight?.board_name || prev.oversight.board_name,
          board_affiliation: data?.oversight?.board_affiliation || prev.oversight.board_affiliation,
          board_contact: data?.oversight?.board_contact || prev.oversight.board_contact,
          data_monitoring_fda_regulatory_intervention: formatYesNo(data?.oversight?.data_monitoring_committee?.fda_regulatory_intervention) || prev.oversight.data_monitoring_fda_regulatory_intervention,
          data_monitoring_section801_clinical_trials: formatYesNo(data?.oversight?.data_monitoring_committee?.section801_clinical_trials) || prev.oversight.data_monitoring_section801_clinical_trials,
        },
        study_description: {
          ...prev.study_description,
          brief_summary: data?.study_description?.brief_summary || prev.study_description.brief_summary,
          detailed_description: data?.study_description?.detailed_description || prev.study_description.detailed_description,
        },
        condition_keywords: {
          ...prev.condition_keywords,
          primary_condition: data?.condition_keywords?.primary_condition || prev.condition_keywords.primary_condition,
          keywords: Array.isArray(data?.condition_keywords?.keywords) 
            ? data.condition_keywords.keywords.join(', ') 
            : prev.condition_keywords.keywords,
        },
        study_design: {
          interventional: {
            ...prev.study_design.interventional,
            primary_purpose: data?.study_design?.interventional?.primary_purpose || prev.study_design.interventional.primary_purpose,
            study_phase: data?.study_design?.interventional?.study_phase || prev.study_design.interventional.study_phase,
            study_model: data?.study_design?.interventional?.study_model || prev.study_design.interventional.study_model,
            study_model_description: data?.study_design?.interventional?.study_model_description || prev.study_design.interventional.study_model_description,
            number_of_arms: data?.study_design?.interventional?.number_of_arms ?? prev.study_design.interventional.number_of_arms,
            masking_enabled: formatYesNo(data?.study_design?.interventional?.masking?.enabled) || prev.study_design.interventional.masking_enabled,
            masking_roles: data?.study_design?.interventional?.masking?.roles || prev.study_design.interventional.masking_roles,
            masking_description: data?.study_design?.interventional?.masking?.description || prev.study_design.interventional.masking_description,
            allocation: data?.study_design?.interventional?.allocation || prev.study_design.interventional.allocation,
            enrollment: data?.study_design?.interventional?.enrollment ?? prev.study_design.interventional.enrollment,
          },
          observational: {
            ...prev.study_design.observational,
            study_model: data?.study_design?.observational?.study_model || prev.study_design.observational.study_model,
            time_perspective: data?.study_design?.observational?.time_perspective || prev.study_design.observational.time_perspective,
            biospecimen_retention: data?.study_design?.observational?.biospecimen_retention || prev.study_design.observational.biospecimen_retention,
            biospecimen_description: data?.study_design?.observational?.biospecimen_description || prev.study_design.observational.biospecimen_description,
            enrollment: data?.study_design?.observational?.enrollment ?? prev.study_design.observational.enrollment,
            target_followup_duration: data?.study_design?.observational?.target_followup_duration || prev.study_design.observational.target_followup_duration,
            number_of_groups: data?.study_design?.observational?.number_of_groups ?? prev.study_design.observational.number_of_groups,
          },
        },
        arms_groups_interventions: {
          arms: data?.arms_groups_interventions?.arms?.length ? data.arms_groups_interventions.arms : prev.arms_groups_interventions.arms,
          groups: data?.arms_groups_interventions?.groups?.length ? data.arms_groups_interventions.groups : prev.arms_groups_interventions.groups,
          interventions: data?.arms_groups_interventions?.interventions?.length ? data.arms_groups_interventions.interventions : prev.arms_groups_interventions.interventions,
        },
        outcomes: {
          primary: data?.outcomes?.primary?.length ? data.outcomes.primary : prev.outcomes.primary,
          secondary: data?.outcomes?.secondary?.length ? data.outcomes.secondary : prev.outcomes.secondary,
          other: data?.outcomes?.other?.length ? data.outcomes.other : prev.outcomes.other,
        },
        eligibility: {
          ...prev.eligibility,
          sex: data?.eligibility?.sex || prev.eligibility.sex,
          gender_based: formatYesNo(data?.eligibility?.gender_based) || prev.eligibility.gender_based,
          gender_eligibility_description: data?.eligibility?.gender_eligibility_description || prev.eligibility.gender_eligibility_description,
          minimum_age: data?.eligibility?.minimum_age ?? prev.eligibility.minimum_age,
          minimum_age_unit: data?.eligibility?.minimum_age_unit || prev.eligibility.minimum_age_unit,
          maximum_age: data?.eligibility?.maximum_age ?? prev.eligibility.maximum_age,
          maximum_age_unit: data?.eligibility?.maximum_age_unit || prev.eligibility.maximum_age_unit,
          accept_healthy_volunteers: formatYesNo(data?.eligibility?.accept_healthy_volunteers) || prev.eligibility.accept_healthy_volunteers,
          eligibility_criteria: data?.eligibility?.eligibility_criteria || prev.eligibility.eligibility_criteria,
          study_population_description: data?.eligibility?.study_population_description || prev.eligibility.study_population_description,
          sampling_method: data?.eligibility?.sampling_method || prev.eligibility.sampling_method,
        },
        contacts_locations_investigators: {
          central_contact: data?.contacts_locations_investigators?.central_contact || prev.contacts_locations_investigators.central_contact,
          central_contact_backup: data?.contacts_locations_investigators?.central_contact_backup || prev.contacts_locations_investigators.central_contact_backup,
          study_officials: data?.contacts_locations_investigators?.study_officials?.length ? data.contacts_locations_investigators.study_officials : prev.contacts_locations_investigators.study_officials,
          facilities: data?.contacts_locations_investigators?.facilities?.length ? data.contacts_locations_investigators.facilities : prev.contacts_locations_investigators.facilities,
        },
        ipd_sharing: {
          ...prev.ipd_sharing,
          plan: data?.ipd_sharing?.plan || prev.ipd_sharing.plan,
          description: data?.ipd_sharing?.description || prev.ipd_sharing.description,
          supporting_info_type: data?.ipd_sharing?.supporting_info_type || prev.ipd_sharing.supporting_info_type,
          timeframe: data?.ipd_sharing?.timeframe || prev.ipd_sharing.timeframe,
          access_criteria: data?.ipd_sharing?.access_criteria || prev.ipd_sharing.access_criteria,
          url: data?.ipd_sharing?.url || prev.ipd_sharing.url,
          reason_if_no: data?.ipd_sharing?.reason_if_no || prev.ipd_sharing.reason_if_no,
        },
        references: {
          citations: data?.references?.citations?.length ? data.references.citations : prev.references.citations,
          links: data?.references?.links?.length ? data.references.links : prev.references.links,
          available_ipd_information: data?.references?.available_ipd_information?.length ? data.references.available_ipd_information : prev.references.available_ipd_information,
        },
      }));
      setSuccess(true);
    } catch (err) {
      setError(err?.message || 'Failed to auto-fill from protocol');
    } finally {
      setLoading(false);
    }
  };

  // REMOVED DUPLICATES: handleAutoPopulateFromCoreDatasheet and handleCloseSnackbar (already defined earlier at lines ~1438 and ~1492)

  const handleExportXML = async () => {
    try {
      if (!projectId) {
        setError('Please select a project before exporting XML.');
        return;
      }

      // Get org name - in real implementation, this might come from user settings or project data
      const orgName = formData.study_identification?.unique_protocol_id ? '' : '';
      
      // Convert form data to XML
      const xmlContent = convertFormDataToXML(formData, orgName);
      
      // Save XML to database
      try {
        await protocolRegistrationService.saveXML(projectId, xmlContent, 'clinicaltrials');
      } catch (saveError) {
        console.error('Error saving XML to database:', saveError);
        // Continue with download even if save fails
      }
      
      // Create a blob with the XML content
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename based on protocol ID or use default
      const protocolId = formData.study_identification?.unique_protocol_id || 'clinical_trial';
      const filename = `clinical_trials_gov_${protocolId}_${new Date().toISOString().split('T')[0]}.xml`;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('XML file exported and saved successfully!');
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error exporting XML:', err);
      setError('Failed to export XML. Please try again.');
    }
  };

  const showWithdrawalReason = useMemo(
    () => formData.study_status.overall_recruitment_status === 'Withdrawn',
    [formData.study_status.overall_recruitment_status]
  );

  if (!projectId) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        Select or create a project to manage ClinicalTrials.gov registration details.
      </Alert>
    );
  }

  const studyDesign = formData?.study_design || initialForm.study_design;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ py: 3, px: 2 }}>
      <Typography variant="h5" gutterBottom>
        ClinicalTrials.gov Registration
      </Typography>

      <Stack spacing={2} sx={{ mb: 3 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">Saved successfully.</Alert>}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={handleAutoFill} disabled={loading || saving}>
            Auto-fill from Protocol
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={autoPopulating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            onClick={handleAutoPopulateFromCoreDatasheet}
            disabled={autoPopulating || loading || saving}
            title="Auto-populate from Core Datasheet"
          >
            {autoPopulating ? 'Auto-Populating...' : 'Auto-Populate from Core Datasheet'}
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={loading || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleExportXML}
            disabled={loading || saving}
          >
            Export XML
          </Button>
        </Box>
      </Stack>

      <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
        1) Study Identification
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Unique Protocol Identification Number"
            value={formData.study_identification.unique_protocol_id}
            onChange={handleIdentificationChange('unique_protocol_id')}
            required
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Brief Title (lay public)"
            value={formData.study_identification.brief_title}
            onChange={handleIdentificationChange('brief_title')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Official Title"
            value={formData.study_identification.official_title}
            onChange={handleIdentificationChange('official_title')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Secondary ID Type"
            value={formData.study_identification.secondary_id_type}
            onChange={handleIdentificationChange('secondary_id_type')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Secondary ID Description"
            value={formData.study_identification.secondary_id_description}
            onChange={handleIdentificationChange('secondary_id_description')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Study Type"
            value={formData.study_identification.study_type}
            onChange={handleIdentificationChange('study_type')}
            fullWidth
            disabled={loading || saving}
          >
            <MenuItem value="Interventional">Interventional</MenuItem>
            <MenuItem value="Observational">Observational</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        2) Study Status
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Record Verification Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.study_status.record_verification_date}
            onChange={handleStatusChange('record_verification_date')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            select
            label="Overall Recruitment Status"
            value={formData.study_status.overall_recruitment_status}
            onChange={handleStatusChange('overall_recruitment_status')}
            fullWidth
            disabled={loading || saving}
          >
            {recruitmentStatusOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {showWithdrawalReason && (
          <Grid item xs={12}>
            <TextField
              label="If withdrawn, why did the study stop?"
              value={formData.study_status.withdrawal_reason}
              onChange={handleStatusChange('withdrawal_reason')}
              fullWidth
              multiline
              minRows={2}
              disabled={loading || saving}
            />
          </Grid>
        )}
        <Grid item xs={12} md={4}>
          <TextField
            label="Study Start Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.study_status.study_start_date}
            onChange={handleStatusChange('study_start_date')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Primary Completion Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.study_status.primary_completion_date}
            onChange={handleStatusChange('primary_completion_date')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Study Completion Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.study_status.study_completion_date}
            onChange={handleStatusChange('study_completion_date')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        3) Sponsors / Collaborators
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Investigator Name"
            value={formData.sponsors_collaborators.investigator_name}
            onChange={handleSponsorsChange('investigator_name')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Investigator Official Title"
            value={formData.sponsors_collaborators.investigator_title}
            onChange={handleSponsorsChange('investigator_title')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Investigator Affiliation"
            value={formData.sponsors_collaborators.investigator_affiliation}
            onChange={handleSponsorsChange('investigator_affiliation')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Sponsor Name"
            value={formData.sponsors_collaborators.sponsor_name}
            onChange={handleSponsorsChange('sponsor_name')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Collaborators (comma-separated)"
            value={formData.sponsors_collaborators.collaborators}
            onChange={handleSponsorsChange('collaborators')}
            fullWidth
            disabled={loading || saving}
            helperText="Enter multiple collaborators separated by commas"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        4) Oversight
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Studies a US FDA Regulated Drug Product"
            value={formData.oversight.fda_regulated_drug_product}
            onChange={handleOversightChange('fda_regulated_drug_product')}
            fullWidth
            disabled={loading || saving}
          >
            {yesNoOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Studies a US FDA Regulated Device Product"
            value={formData.oversight.fda_regulated_device_product}
            onChange={handleOversightChange('fda_regulated_device_product')}
            fullWidth
            disabled={loading || saving}
          >
            {yesNoOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="U.S. FDA IND or IDE"
            value={formData.oversight.has_ind_ide}
            onChange={handleOversightChange('has_ind_ide')}
            fullWidth
            disabled={loading || saving}
          >
            {yesNoOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="IND/IDE Number"
            value={formData.oversight.ind_ide_number}
            onChange={handleOversightChange('ind_ide_number')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="IND Serial Number"
            value={formData.oversight.ind_serial_number}
            onChange={handleOversightChange('ind_serial_number')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Availability to Expanded Access"
            value={formData.oversight.expanded_access}
            onChange={handleOversightChange('expanded_access')}
            fullWidth
            disabled={loading || saving}
          >
            {expandedAccessOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Product Manufactured in and Exported from the US"
            value={formData.oversight.product_manufactured_exported_us}
            onChange={handleOversightChange('product_manufactured_exported_us')}
            fullWidth
            disabled={loading || saving}
          >
            {yesNoOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Human Subject Review"
            value={formData.oversight.human_subject_review}
            onChange={handleOversightChange('human_subject_review')}
            fullWidth
            multiline
            minRows={2}
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Human Subject Protection Review Board Status"
            value={formData.oversight.human_subject_protection_status}
            onChange={handleOversightChange('human_subject_protection_status')}
            fullWidth
            disabled={loading || saving}
          >
            {irbStatusOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Board Approval Number"
            value={formData.oversight.board_approval_number}
            onChange={handleOversightChange('board_approval_number')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Board Name"
            value={formData.oversight.board_name}
            onChange={handleOversightChange('board_name')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Board Affiliation"
            value={formData.oversight.board_affiliation}
            onChange={handleOversightChange('board_affiliation')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Board Contact"
            value={formData.oversight.board_contact}
            onChange={handleOversightChange('board_contact')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Data Monitoring Committee: FDA Regulatory Intervention"
            value={formData.oversight.data_monitoring_fda_regulatory_intervention}
            onChange={handleOversightChange('data_monitoring_fda_regulatory_intervention')}
            fullWidth
            disabled={loading || saving}
          >
            {yesNoOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Data Monitoring Committee: Section 801 Clinical Trials"
            value={formData.oversight.data_monitoring_section801_clinical_trials}
            onChange={handleOversightChange('data_monitoring_section801_clinical_trials')}
            fullWidth
            disabled={loading || saving}
          >
            {yesNoOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        5) Study Description
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            label="Brief Summary"
            value={formData.study_description.brief_summary}
            onChange={handleStudyDescriptionChange('brief_summary')}
            fullWidth
            multiline
            minRows={3}
            disabled={loading || saving}
            inputProps={{ maxLength: 5000 }}
            helperText="Limit 5000 characters"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Detailed Description"
            value={formData.study_description.detailed_description}
            onChange={handleStudyDescriptionChange('detailed_description')}
            fullWidth
            multiline
            minRows={4}
            disabled={loading || saving}
            inputProps={{ maxLength: 32000 }}
            helperText="Limit 32000 characters"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        6) Condition and Keywords
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Primary Disease / Condition"
            value={formData.condition_keywords.primary_condition}
            onChange={handleConditionChange('primary_condition')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Keywords (comma-separated)"
            value={formData.condition_keywords.keywords}
            onChange={handleConditionChange('keywords')}
            fullWidth
            disabled={loading || saving}
            helperText="Enter multiple keywords separated by commas"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        9) Outcome Measures
      </Typography>
      {['primary', 'secondary', 'other'].map((sectionKey) => (
        <Box key={sectionKey} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, textTransform: 'capitalize' }}>
            {sectionKey} Outcome Measures
          </Typography>
          {(formData.outcomes?.[sectionKey] || initialForm.outcomes[sectionKey]).map((item, idx) => (
            <Grid container spacing={2} sx={{ mb: 1 }} key={`${sectionKey}-${idx}`}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Title"
                  value={item.title}
                  onChange={handleOutcomeChange(sectionKey, idx, 'title')}
                  fullWidth
                  disabled={loading || saving}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  label="Description"
                  value={item.description}
                  onChange={handleOutcomeChange(sectionKey, idx, 'description')}
                  fullWidth
                  disabled={loading || saving}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Timeframe"
                  value={item.timeframe}
                  onChange={handleOutcomeChange(sectionKey, idx, 'timeframe')}
                  fullWidth
                  disabled={loading || saving}
                />
              </Grid>
            </Grid>
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={addOutcomeRow(sectionKey)}
            disabled={loading || saving}
          >
            Add {sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1)} Outcome
          </Button>
        </Box>
      ))}

      <Typography variant="h6" gutterBottom>
        10) Eligibility
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Sex"
            value={formData.eligibility.sex}
            onChange={handleEligibilityChange('sex')}
            fullWidth
            disabled={loading || saving}
          >
            {['All', 'Male', 'Female'].map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Gender Based"
            value={formData.eligibility.gender_based}
            onChange={handleEligibilityChange('gender_based')}
            fullWidth
            disabled={loading || saving}
          >
            {yesNoOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Gender Eligibility Description"
            value={formData.eligibility.gender_description}
            onChange={handleEligibilityChange('gender_description')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Minimum Age"
            type="number"
            value={formData.eligibility.age_min}
            onChange={handleEligibilityChange('age_min')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            select
            label="Min Age Unit"
            value={formData.eligibility.age_min_unit}
            onChange={handleEligibilityChange('age_min_unit')}
            fullWidth
            disabled={loading || saving}
          >
            {['Years', 'Months', 'Weeks', 'Days', 'Hours', 'Minutes', 'No Limit'].map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Maximum Age"
            type="number"
            value={formData.eligibility.age_max}
            onChange={handleEligibilityChange('age_max')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            select
            label="Max Age Unit"
            value={formData.eligibility.age_max_unit}
            onChange={handleEligibilityChange('age_max_unit')}
            fullWidth
            disabled={loading || saving}
          >
            {['Years', 'Months', 'Weeks', 'Days', 'Hours', 'Minutes', 'No Limit'].map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Accept Healthy Volunteers"
            value={formData.eligibility.healthy_volunteers}
            onChange={handleEligibilityChange('healthy_volunteers')}
            fullWidth
            disabled={loading || saving}
            helperText="Optional for observational studies"
          >
            {yesNoOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Eligibility Criteria"
            value={formData.eligibility.eligibility_criteria}
            onChange={handleEligibilityChange('eligibility_criteria')}
            fullWidth
            disabled={loading || saving}
            multiline
            minRows={3}
            inputProps={{ maxLength: 20000 }}
            helperText="Limit 20000 characters"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Study Population Description (Observational)"
            value={formData.eligibility.study_population_description}
            onChange={handleEligibilityChange('study_population_description')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Sampling Method (Observational)"
            value={formData.eligibility.sampling_method}
            onChange={handleEligibilityChange('sampling_method')}
            fullWidth
            disabled={loading || saving}
          >
            {['Probability Samples', 'Non Probability Samples'].map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>


      {formData.study_identification.study_type === 'Interventional' && (
        <>
          <Typography variant="h6" gutterBottom>
            8) Arms (Interventional)
          </Typography>
          {formData.arms_groups_interventions.arms.map((arm, idx) => (
            <Grid container spacing={2} sx={{ mb: 2 }} key={`arm-${idx}`}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Arm Title"
                  value={arm.title}
                  onChange={handleArmsGroupsChange('arms', idx, 'title')}
                  fullWidth
                  disabled={loading || saving}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Arm Type"
                  value={arm.type}
                  onChange={handleArmsGroupsChange('arms', idx, 'type')}
                  fullWidth
                  disabled={loading || saving}
                >
                  {armTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Arm Description"
                  value={arm.description}
                  onChange={handleArmsGroupsChange('arms', idx, 'description')}
                  fullWidth
                  disabled={loading || saving}
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>
          ))}
          <Button
            variant="outlined"
            size="small"
            sx={{ mb: 3 }}
            onClick={addArmsGroupsRow('arms', { title: '', type: '', description: '' })}
            disabled={loading || saving}
          >
            Add Arm
          </Button>
        </>
      )}

      {formData.study_identification.study_type === 'Observational' && (
        <>
          <Typography variant="h6" gutterBottom>
            Groups / Cohorts (Observational)
          </Typography>
          {formData.arms_groups_interventions.groups.map((group, idx) => (
            <Grid container spacing={2} sx={{ mb: 2 }} key={`group-${idx}`}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Group / Cohort Label"
                  value={group.label}
                  onChange={handleArmsGroupsChange('groups', idx, 'label')}
                  fullWidth
                  disabled={loading || saving}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Group / Cohort Description"
                  value={group.description}
                  onChange={handleArmsGroupsChange('groups', idx, 'description')}
                  fullWidth
                  disabled={loading || saving}
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>
          ))}
          <Button
            variant="outlined"
            size="small"
            sx={{ mb: 3 }}
            onClick={addArmsGroupsRow('groups', { label: '', description: '' })}
            disabled={loading || saving}
          >
            Add Group / Cohort
          </Button>
        </>
      )}

      <Typography variant="h6" gutterBottom>
        Interventions
      </Typography>
      {formData.arms_groups_interventions.interventions.map((intv, idx) => (
        <Grid container spacing={2} sx={{ mb: 2 }} key={`intervention-${idx}`}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Intervention Type"
              value={intv.intervention_type}
              onChange={handleArmsGroupsChange('interventions', idx, 'intervention_type')}
              fullWidth
              disabled={loading || saving}
            >
              {interventionTypes.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Intervention Name"
              value={intv.name}
              onChange={handleArmsGroupsChange('interventions', idx, 'name')}
              fullWidth
              disabled={loading || saving}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Arm/Group Cross Reference"
              value={intv.arm_group_cross_reference}
              onChange={handleArmsGroupsChange('interventions', idx, 'arm_group_cross_reference')}
              fullWidth
              disabled={loading || saving}
              helperText="Reference arm/group label or title"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Intervention Description"
              value={intv.description}
              onChange={handleArmsGroupsChange('interventions', idx, 'description')}
              fullWidth
              disabled={loading || saving}
              multiline
              minRows={2}
            />
          </Grid>
        </Grid>
      ))}
      <Button
        variant="outlined"
        size="small"
        sx={{ mb: 3 }}
        onClick={addArmsGroupsRow('interventions', { intervention_type: '', name: '', description: '', arm_group_cross_reference: '' })}
        disabled={loading || saving}
      >
        Add Intervention
      </Button>

      {formData.study_identification.study_type === 'Interventional' && (
        <>
          <Typography variant="h6" gutterBottom>
            7) Interventional Study Design
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Primary Purpose"
                value={studyDesign.interventional?.primary_purpose || ''}
                onChange={handleStudyDesignChange('interventional', 'primary_purpose')}
                fullWidth
                disabled={loading || saving}
              >
                {primaryPurposeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Study Phase"
                value={studyDesign.interventional?.study_phase || ''}
                onChange={handleStudyDesignChange('interventional', 'study_phase')}
                fullWidth
                disabled={loading || saving}
              >
                {studyPhaseOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Interventional Study Model"
                value={studyDesign.interventional?.study_model || ''}
                onChange={handleStudyDesignChange('interventional', 'study_model')}
                fullWidth
                disabled={loading || saving}
              >
                {interventionalModelOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Interventional Study Model Description"
                value={studyDesign.interventional?.study_model_description || ''}
                onChange={handleStudyDesignChange('interventional', 'study_model_description')}
                fullWidth
                disabled={loading || saving}
                inputProps={{ maxLength: 1000 }}
                helperText="Limit 1000 characters"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Number of Arms"
                type="number"
                value={studyDesign.interventional?.number_of_arms ?? ''}
                onChange={handleStudyDesignChange('interventional', 'number_of_arms')}
                fullWidth
                disabled={loading || saving}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Masking / Blinding"
                value={studyDesign.interventional?.masking_enabled || ''}
                onChange={handleStudyDesignChange('interventional', 'masking_enabled')}
                fullWidth
                disabled={loading || saving}
              >
                {yesNoOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Allocation"
                value={studyDesign.interventional?.allocation || ''}
                onChange={handleStudyDesignChange('interventional', 'allocation')}
                fullWidth
                disabled={loading || saving}
              >
                {allocationOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {studyDesign.interventional?.masking_enabled === 'Yes' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Masking Roles
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={studyDesign.interventional?.masking_roles?.participants || false}
                          onChange={handleMaskingRoleChange('participants')}
                          disabled={loading || saving}
                        />
                      }
                      label="Participants"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={studyDesign.interventional?.masking_roles?.care_providers || false}
                          onChange={handleMaskingRoleChange('care_providers')}
                          disabled={loading || saving}
                        />
                      }
                      label="Care Providers"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={studyDesign.interventional?.masking_roles?.investigators || false}
                          onChange={handleMaskingRoleChange('investigators')}
                          disabled={loading || saving}
                        />
                      }
                      label="Investigators"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={studyDesign.interventional?.masking_roles?.outcomes_assessors || false}
                          onChange={handleMaskingRoleChange('outcomes_assessors')}
                          disabled={loading || saving}
                        />
                      }
                      label="Outcomes Assessors"
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Masking Description"
                    value={studyDesign.interventional?.masking_description || ''}
                    onChange={handleStudyDesignChange('interventional', 'masking_description')}
                    fullWidth
                    disabled={loading || saving}
                    multiline
                    minRows={2}
                    inputProps={{ maxLength: 1000 }}
                    helperText="Limit 1000 characters"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={4}>
              <TextField
                label="Enrollment (Total Participants)"
                type="number"
                value={studyDesign.interventional?.enrollment ?? ''}
                onChange={handleStudyDesignChange('interventional', 'enrollment')}
                fullWidth
                disabled={loading || saving}
              />
            </Grid>
          </Grid>
        </>
      )}

      {formData.study_identification.study_type === 'Observational' && (
        <>
          <Typography variant="h6" gutterBottom>
            7) Observational Study Design
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Observational Study Model"
                value={studyDesign.observational?.study_model || ''}
                onChange={handleStudyDesignChange('observational', 'study_model')}
                fullWidth
                disabled={loading || saving}
              >
                {observationalModelOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Time Perspective"
                value={studyDesign.observational?.time_perspective || ''}
                onChange={handleStudyDesignChange('observational', 'time_perspective')}
                fullWidth
                disabled={loading || saving}
              >
                {timePerspectiveOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Biospecimen Retention"
                value={studyDesign.observational?.biospecimen_retention || ''}
                onChange={handleStudyDesignChange('observational', 'biospecimen_retention')}
                fullWidth
                disabled={loading || saving}
              >
                {biospecimenRetentionOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Biospecimen Description"
                value={studyDesign.observational?.biospecimen_description || ''}
                onChange={handleStudyDesignChange('observational', 'biospecimen_description')}
                fullWidth
                disabled={loading || saving}
                multiline
                minRows={2}
                inputProps={{ maxLength: 1000 }}
                helperText="Limit 1000 characters"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Enrollment"
                type="number"
                value={studyDesign.observational?.enrollment ?? ''}
                onChange={handleStudyDesignChange('observational', 'enrollment')}
                fullWidth
                disabled={loading || saving}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Target Follow-up Duration"
                value={studyDesign.observational?.target_followup_duration || ''}
                onChange={handleStudyDesignChange('observational', 'target_followup_duration')}
                fullWidth
                disabled={loading || saving}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Number of Groups / Cohorts"
                type="number"
                value={studyDesign.observational?.number_of_groups ?? ''}
                onChange={handleStudyDesignChange('observational', 'number_of_groups')}
                fullWidth
                disabled={loading || saving}
              />
            </Grid>
          </Grid>
        </>
      )}

      <Typography variant="h6" gutterBottom>
        11) Contacts, Locations, Investigators
      </Typography>
      <Typography variant="subtitle1" sx={{ mt: 1 }}>
        Central Contact
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {['first_name','middle_initials','last_name','degree','phone','phone_ext','email'].map((field) => (
          <Grid item xs={12} md={4} key={`central-${field}`}>
            <TextField
              label={field.replace(/_/g, ' ')}
              value={formData.contacts_locations_investigators.central_contact[field]}
              onChange={handleContactChange('central_contact', field)}
              fullWidth
              disabled={loading || saving}
            />
          </Grid>
        ))}
      </Grid>
      <Typography variant="subtitle1" sx={{ mt: 1 }}>
        Central Contact Backup
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {['first_name','middle_initials','last_name','degree','phone','phone_ext','email'].map((field) => (
          <Grid item xs={12} md={4} key={`centralb-${field}`}>
            <TextField
              label={field.replace(/_/g, ' ')}
              value={formData.contacts_locations_investigators.central_contact_backup[field]}
              onChange={handleContactChange('central_contact_backup', field)}
              fullWidth
              disabled={loading || saving}
            />
          </Grid>
        ))}
      </Grid>

      <Typography variant="subtitle1" sx={{ mt: 1 }}>
        Overall Study Officials
      </Typography>
      {formData.contacts_locations_investigators.study_officials.map((off, idx) => (
        <Grid container spacing={2} sx={{ mb: 2 }} key={`official-${idx}`}>
          <Grid item xs={12} md={3}>
            <TextField label="First Name" value={off.first_name} onChange={handleStudyOfficialChange(idx, 'first_name')} fullWidth disabled={loading || saving} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Middle Initials" value={off.middle_initials} onChange={handleStudyOfficialChange(idx, 'middle_initials')} fullWidth disabled={loading || saving} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Last Name" value={off.last_name} onChange={handleStudyOfficialChange(idx, 'last_name')} fullWidth disabled={loading || saving} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Degree" value={off.degree} onChange={handleStudyOfficialChange(idx, 'degree')} fullWidth disabled={loading || saving} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Affiliation" value={off.affiliation} onChange={handleStudyOfficialChange(idx, 'affiliation')} fullWidth disabled={loading || saving} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Role"
              value={off.role}
              onChange={handleStudyOfficialChange(idx, 'role')}
              fullWidth
              disabled={loading || saving}
            >
              {['Study Chair','Study Director','Study Principal Investigator'].map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      ))}
      <Button variant="outlined" size="small" sx={{ mb: 3 }} onClick={addStudyOfficialRow} disabled={loading || saving}>
        Add Study Official
      </Button>

      <Typography variant="subtitle1" sx={{ mt: 1 }}>
        Facilities
      </Typography>
      {formData.contacts_locations_investigators.facilities.map((fac, fidx) => (
        <Box key={`facility-${fidx}`} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              ['name','Facility Name'],
              ['city','City'],
              ['state','State'],
              ['postal_code','Postal Code'],
              ['country','Country'],
            ].map(([field,label]) => (
              <Grid item xs={12} md={4} key={`${fidx}-${field}`}>
                <TextField label={label} value={fac[field]} onChange={handleFacilityChange(fidx, field)} fullWidth disabled={loading || saving} />
              </Grid>
            ))}
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Individual Site Status"
                value={fac.individual_site_status}
                onChange={handleFacilityChange(fidx, 'individual_site_status')}
                fullWidth
                disabled={loading || saving}
              >
                {['Not yet Recruiting','Recruiting','Enrolling by invitation','Active-not recruiting','Completed','Suspended','Terminated','Withdrawn'].map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Typography variant="subtitle2">Facility Contact</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {['first_name','middle_initials','last_name','degree','phone','phone_ext','email'].map((field) => (
              <Grid item xs={12} md={4} key={`fc-${fidx}-${field}`}>
                <TextField
                  label={field.replace(/_/g,' ')}
                  value={fac.contact?.[field] || ''}
                  onChange={handleFacilityContactChange(fidx, 'contact', field)}
                  fullWidth
                  disabled={loading || saving}
                />
              </Grid>
            ))}
          </Grid>
          <Typography variant="subtitle2">Facility Contact Backup</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {['first_name','middle_initials','last_name','degree','phone','phone_ext','email'].map((field) => (
              <Grid item xs={12} md={4} key={`fcb-${fidx}-${field}`}>
                <TextField
                  label={field.replace(/_/g,' ')}
                  value={fac.contact_backup?.[field] || ''}
                  onChange={handleFacilityContactChange(fidx, 'contact_backup', field)}
                  fullWidth
                  disabled={loading || saving}
                />
              </Grid>
            ))}
          </Grid>
          <Typography variant="subtitle2">Investigators</Typography>
          {fac.investigators.map((inv, iidx) => (
            <Grid container spacing={2} sx={{ mb: 1 }} key={`inv-${fidx}-${iidx}`}>
              <Grid item xs={12} md={3}>
                <TextField label="First Name" value={inv.first_name} onChange={handleFacilityInvestigatorChange(fidx, iidx, 'first_name')} fullWidth disabled={loading || saving} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField label="Middle Initials" value={inv.middle_initials} onChange={handleFacilityInvestigatorChange(fidx, iidx, 'middle_initials')} fullWidth disabled={loading || saving} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField label="Last Name" value={inv.last_name} onChange={handleFacilityInvestigatorChange(fidx, iidx, 'last_name')} fullWidth disabled={loading || saving} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField label="Degree" value={inv.degree} onChange={handleFacilityInvestigatorChange(fidx, iidx, 'degree')} fullWidth disabled={loading || saving} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Role"
                  value={inv.role}
                  onChange={handleFacilityInvestigatorChange(fidx, iidx, 'role')}
                  fullWidth
                  disabled={loading || saving}
                >
                  {['Site Principal Investigator','Site Sub-Investigator'].map((r) => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          ))}
          <Button variant="outlined" size="small" sx={{ mb: 1 }} onClick={addFacilityInvestigatorRow(fidx)} disabled={loading || saving}>
            Add Facility Investigator
          </Button>
        </Box>
      ))}
      <Button variant="outlined" size="small" sx={{ mb: 3 }} onClick={addFacilityRow} disabled={loading || saving}>
        Add Facility
      </Button>

      <Typography variant="h6" gutterBottom>
        12) IPD Sharing Statement
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Plan to Share IPD"
            value={formData.ipd_sharing.plan}
            onChange={handleIpdChange('plan')}
            fullWidth
            disabled={loading || saving}
          >
            {['Yes','No','Undecided'].map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            label="IPD Sharing Plan Description"
            value={formData.ipd_sharing.description}
            onChange={handleIpdChange('description')}
            fullWidth
            disabled={loading || saving}
            multiline
            minRows={2}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="IPD Supporting Information Types (comma-separated)"
            value={formData.ipd_sharing.supporting_info_types}
            onChange={handleIpdChange('supporting_info_types')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="IPD Sharing Timeframe"
            value={formData.ipd_sharing.timeframe}
            onChange={handleIpdChange('timeframe')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="IPD Sharing Access Criteria"
            value={formData.ipd_sharing.access_criteria}
            onChange={handleIpdChange('access_criteria')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="IPD Sharing URL"
            value={formData.ipd_sharing.url}
            onChange={handleIpdChange('url')}
            fullWidth
            disabled={loading || saving}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="If No/Undecided, explain"
            value={formData.ipd_sharing.reason_if_no}
            onChange={handleIpdChange('reason_if_no')}
            fullWidth
            disabled={loading || saving}
            multiline
            minRows={2}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        13) References
      </Typography>
      <Typography variant="subtitle1">Citations</Typography>
      {formData.references.citations.map((c, idx) => (
        <Grid container spacing={2} sx={{ mb: 2 }} key={`cit-${idx}`}>
          <Grid item xs={12} md={3}>
            <TextField label="PubMed ID" value={c.pubmed_id} onChange={handleReferenceChange('citations', idx, 'pubmed_id')} fullWidth disabled={loading || saving} />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField label="Citation" value={c.citation} onChange={handleReferenceChange('citations', idx, 'citation')} fullWidth disabled={loading || saving} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Result Reference" value={c.result_reference} onChange={handleReferenceChange('citations', idx, 'result_reference')} fullWidth disabled={loading || saving} />
          </Grid>
        </Grid>
      ))}
      <Button variant="outlined" size="small" sx={{ mb: 2 }} onClick={addReferenceRow('citations', { pubmed_id: '', citation: '', result_reference: '' })} disabled={loading || saving}>
        Add Citation
      </Button>

      <Typography variant="subtitle1" sx={{ mt: 2 }}>Links</Typography>
      {formData.references.links.map((l, idx) => (
        <Grid container spacing={2} sx={{ mb: 2 }} key={`link-${idx}`}>
          <Grid item xs={12} md={6}>
            <TextField label="URL" value={l.url} onChange={handleReferenceChange('links', idx, 'url')} fullWidth disabled={loading || saving} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Description" value={l.description} onChange={handleReferenceChange('links', idx, 'description')} fullWidth disabled={loading || saving} />
          </Grid>
        </Grid>
      ))}
      <Button variant="outlined" size="small" sx={{ mb: 2 }} onClick={addReferenceRow('links', { url: '', description: '' })} disabled={loading || saving}>
        Add Link
      </Button>

      <Typography variant="subtitle1" sx={{ mt: 2 }}>Available IPD Information</Typography>
      {formData.references.available_ipd_information.map((a, idx) => (
        <Grid container spacing={2} sx={{ mb: 2 }} key={`avail-${idx}`}>
          <Grid item xs={12} md={3}>
            <TextField label="Info Type" value={a.info_type} onChange={handleReferenceChange('available_ipd_information', idx, 'info_type')} fullWidth disabled={loading || saving} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="URL" value={a.url} onChange={handleReferenceChange('available_ipd_information', idx, 'url')} fullWidth disabled={loading || saving} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Identifier" value={a.identifier} onChange={handleReferenceChange('available_ipd_information', idx, 'identifier')} fullWidth disabled={loading || saving} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Comment" value={a.comment} onChange={handleReferenceChange('available_ipd_information', idx, 'comment')} fullWidth disabled={loading || saving} />
          </Grid>
        </Grid>
      ))}
      <Button variant="outlined" size="small" sx={{ mb: 4 }} onClick={addReferenceRow('available_ipd_information', { info_type: '', url: '', identifier: '', comment: '' })} disabled={loading || saving}>
        Add Available IPD Info
      </Button>

      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={loading || saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Stack>

      {/* Snackbar for auto-population messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function normalizePayload(form) {
  return {
    study_identification: {
      unique_protocol_id: form.study_identification.unique_protocol_id,
      brief_title: form.study_identification.brief_title,
      official_title: form.study_identification.official_title,
      secondary_id: {
        id_type: form.study_identification.secondary_id_type,
        description: form.study_identification.secondary_id_description,
      },
      study_type: form.study_identification.study_type,
    },
    study_status: {
      record_verification_date: form.study_status.record_verification_date || null,
      overall_recruitment_status: form.study_status.overall_recruitment_status,
      withdrawal_reason: form.study_status.withdrawal_reason,
      study_start_date: form.study_status.study_start_date || null,
      primary_completion_date: form.study_status.primary_completion_date || null,
      study_completion_date: form.study_status.study_completion_date || null,
    },
    sponsors_collaborators: {
      responsible_party: {
        investigator_name: form.sponsors_collaborators.investigator_name,
        official_title: form.sponsors_collaborators.investigator_title,
        affiliation: form.sponsors_collaborators.investigator_affiliation,
      },
      sponsor_name: form.sponsors_collaborators.sponsor_name,
      collaborators: splitList(form.sponsors_collaborators.collaborators),
    },
    oversight: {
      fda_regulated_drug_product: parseYesNo(form.oversight.fda_regulated_drug_product),
      fda_regulated_device_product: parseYesNo(form.oversight.fda_regulated_device_product),
      ind_ide: {
        has_ind_ide: parseYesNo(form.oversight.has_ind_ide),
        ind_ide_number: form.oversight.ind_ide_number,
        ind_serial_number: form.oversight.ind_serial_number,
        expanded_access: form.oversight.expanded_access || undefined,
      },
      product_manufactured_exported_us: parseYesNo(form.oversight.product_manufactured_exported_us),
      human_subject_review: form.oversight.human_subject_review,
      human_subject_protection_status: form.oversight.human_subject_protection_status,
      board_approval_number: form.oversight.board_approval_number,
      board_name: form.oversight.board_name,
      board_affiliation: form.oversight.board_affiliation,
      board_contact: form.oversight.board_contact,
      data_monitoring_committee: {
        fda_regulatory_intervention: parseYesNo(form.oversight.data_monitoring_fda_regulatory_intervention),
        section801_clinical_trials: parseYesNo(form.oversight.data_monitoring_section801_clinical_trials),
      },
    },
    study_description: {
      brief_summary: form.study_description.brief_summary,
      detailed_description: form.study_description.detailed_description,
    },
    condition_keywords: {
      primary_condition: form.condition_keywords.primary_condition,
      keywords: splitList(form.condition_keywords.keywords),
    },
    study_design: {
      interventional: {
        primary_purpose: form.study_design.interventional.primary_purpose,
        study_phase: form.study_design.interventional.study_phase,
        study_model: form.study_design.interventional.study_model,
        study_model_description: form.study_design.interventional.study_model_description,
        number_of_arms: numberOrNull(form.study_design.interventional.number_of_arms),
        masking: {
          enabled: parseYesNo(form.study_design.interventional.masking_enabled),
          roles: {
            participants: !!form.study_design.interventional.masking_roles.participants,
            care_providers: !!form.study_design.interventional.masking_roles.care_providers,
            investigators: !!form.study_design.interventional.masking_roles.investigators,
            outcomes_assessors: !!form.study_design.interventional.masking_roles.outcomes_assessors,
          },
          description: form.study_design.interventional.masking_description,
        },
        allocation: form.study_design.interventional.allocation,
        enrollment: numberOrNull(form.study_design.interventional.enrollment),
      },
      observational: {
        study_model: form.study_design.observational.study_model,
        time_perspective: form.study_design.observational.time_perspective,
        biospecimen_retention: form.study_design.observational.biospecimen_retention,
        biospecimen_description: form.study_design.observational.biospecimen_description,
        enrollment: numberOrNull(form.study_design.observational.enrollment),
        target_followup_duration: form.study_design.observational.target_followup_duration,
        number_of_groups: numberOrNull(form.study_design.observational.number_of_groups),
      },
    },
    arms_groups_interventions: {
      arms: form.arms_groups_interventions?.arms || [],
      groups: form.arms_groups_interventions?.groups || [],
      interventions: form.arms_groups_interventions?.interventions || [],
    },
    outcomes: {
      primary: form.outcomes.primary,
      secondary: form.outcomes.secondary,
      other: form.outcomes.other,
    },
    eligibility: {
      sex: form.eligibility.sex,
      gender_based: parseYesNo(form.eligibility.gender_based),
      gender_description: form.eligibility.gender_description,
      age_min: numberOrNull(form.eligibility.age_min),
      age_min_unit: form.eligibility.age_min_unit,
      age_max: numberOrNull(form.eligibility.age_max),
      age_max_unit: form.eligibility.age_max_unit,
      healthy_volunteers: parseYesNo(form.eligibility.healthy_volunteers),
      eligibility_criteria: form.eligibility.eligibility_criteria,
      study_population_description: form.eligibility.study_population_description,
      sampling_method: form.eligibility.sampling_method,
    },
    contacts_locations_investigators: {
      central_contact: { ...form.contacts_locations_investigators.central_contact },
      central_contact_backup: { ...form.contacts_locations_investigators.central_contact_backup },
      study_officials: form.contacts_locations_investigators.study_officials,
      facilities: form.contacts_locations_investigators.facilities,
    },
    ipd_sharing: {
      plan: form.ipd_sharing.plan,
      description: form.ipd_sharing.description,
      supporting_info_types: splitList(form.ipd_sharing.supporting_info_types),
      timeframe: form.ipd_sharing.timeframe,
      access_criteria: form.ipd_sharing.access_criteria,
      url: form.ipd_sharing.url,
      reason_if_no: form.ipd_sharing.reason_if_no,
    },
    references: {
      citations: form.references.citations,
      links: form.references.links,
      available_ipd_information: form.references.available_ipd_information,
    },
  };
}

function splitList(value = '') {
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

function parseYesNo(value) {
  if (value === 'Yes') return true;
  if (value === 'No') return false;
  return undefined;
}

function formatYesNo(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '';
}

function numberOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
}

export default ClinicalTrialsGov;

